import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import WhatsAppIcon from "@/components/icons/WhatsAppIcon";
import { useMarkCommunityJoinedMutation } from "@/generated/graphql";
import { WHATSAPP_COMMUNITY_URL } from "@/lib/community/communityPrompt";

interface CommunityGateModalProps {
  open: boolean;
  /** Fired when the user dismisses the modal (X / overlay / Esc / "maybe later"). */
  onOpenChange: (open: boolean) => void;
  /** Fired after the user joins (so the parent can refetch + close). */
  onJoined: () => void;
}

/**
 * Dismissible WhatsApp "The Clippers" community prompt — formerly a hard,
 * redirect-based full-page gate (CommunityOnboarding). Now a modal shown only to
 * clipping-channel users who have not joined; see shouldShowCommunityModal.
 */
export const CommunityGateModal = ({ open, onOpenChange, onJoined }: CommunityGateModalProps) => {
  const { t } = useTranslation();
  const [markCommunityJoined, { loading }] = useMarkCommunityJoinedMutation();

  const handleJoin = async () => {
    window.open(WHATSAPP_COMMUNITY_URL, "_blank", "noopener,noreferrer");

    try {
      await markCommunityJoined();
    } catch (error) {
      console.error("Failed to mark community as joined:", error);
    }

    onJoined();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md border-border/50 bg-card/95 backdrop-blur">
        <DialogHeader className="items-center text-center">
          <div className="mx-auto mb-1 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#25D366]/15 text-[#25D366]">
            <WhatsAppIcon className="h-9 w-9" />
          </div>
          <DialogTitle className="text-2xl">{t("communityGate.title")}</DialogTitle>
          <DialogDescription>{t("communityGate.description")}</DialogDescription>
        </DialogHeader>

        <ul className="text-left text-sm text-muted-foreground space-y-2 bg-secondary/30 rounded-lg p-4">
          <li className="flex gap-2">
            <span className="text-[#25D366] font-bold">•</span>
            <span>{t("communityGate.bullet1")}</span>
          </li>
          <li className="flex gap-2">
            <span className="text-[#25D366] font-bold">•</span>
            <span>{t("communityGate.bullet2")}</span>
          </li>
          <li className="flex gap-2">
            <span className="text-[#25D366] font-bold">•</span>
            <span>{t("communityGate.bullet3")}</span>
          </li>
        </ul>

        <Button
          onClick={handleJoin}
          disabled={loading}
          className="w-full h-12 text-base font-semibold bg-[#25D366] hover:bg-[#1ebe5b] text-white"
        >
          <WhatsAppIcon className="h-5 w-5 mr-2" />
          {t("communityGate.cta")}
        </Button>

        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="mx-auto text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          {t("communityGate.dismiss")}
        </button>

        <p className="text-center text-xs text-muted-foreground">{t("communityGate.footer")}</p>
      </DialogContent>
    </Dialog>
  );
};

export default CommunityGateModal;
